import { ApiClient } from '@provider-portal/common/api';
import { GetAccountInfoResponse } from '@provider-portal/common/api/gen/DeliveryProviderAuthApi';
import { combineEpics } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { selectAssignedProviderIds } from 'rdx/account/selectors';
import { GlobalAppActions } from 'rdx/actions';
import { AppServices } from 'rdx/middleware';
import { getScheduleAsyncAction } from 'rdx/schedule';
import { GlobalAppState } from 'rdx/state';
import { getMainEntryWidgetUrlAsyncAction } from 'rdx/support';
import { requestTargetingParametersAsyncAction } from 'rdx/targetingParameters/actions';

import {
  requestAccountCampaignInfoAsyncAction,
  requestAccountInfoAsyncAction,
  selectProviderAction,
} from './actions';
import {
  GetProviderPortalAccountInfoResponse,
  GetProviderPortalCampaignAccountInfoResponse,
  ProviderPortalAssignedProvider,
  VendorAssignedProvider,
} from './types';

const getAccountInfo = async (
  apiClient: ApiClient,
): Promise<GetProviderPortalAccountInfoResponse> => {
  const getAccountInfoResponse: GetAccountInfoResponse = await apiClient.provider.getAccountInfo();
  const providerAssignments: ProviderPortalAssignedProvider[] =
    getAccountInfoResponse.account_info.assignments.map((assignment) => {
      return {
        provider_id: assignment.provider_id,
        provider_name: assignment.provider_name,
        vendor_id: assignment.vendor_id,
        vendor_name: assignment.vendor_name,
        city_id: assignment.city_id,
        country_code: assignment.country_code,
        currency: assignment.currency,
        price_format_setting: assignment.price_format_setting,
        city_locales: assignment.city_locales,
        city_main_locale: assignment.city_main_locale,
        campaigns_settings: {
          can_self_assign_campaign: assignment.food_partner_portal_setup.can_self_assign_campaign,
          can_manage_promotions: assignment.food_partner_portal_setup.can_manage_promotions,
          is_menu_inflation: assignment.food_partner_portal_setup.is_menu_inflation,
          is_metrics_satisfied: assignment.food_partner_portal_setup.is_metrics_satisfied,
          // TODO : integrate getAccountInfo CAMLE-2907
          can_setup_bolt_plus_promotions: false,
          can_manage_smart_promotions: Boolean(
            assignment.food_partner_portal_setup.can_manage_smart_promotions,
          ),
        },
        has_menu_editor: assignment.has_menu_editor,
        menu_editor_settings: assignment.menu_editor_settings,
        dine_in_settings: assignment.dine_in_settings,
        deductions_report_settings: assignment.deductions_report_settings,
        new_merchant_venue_settings: assignment.new_merchant_venue_settings,
      };
    });

  const assignedProvidersByVendorId: Map<number, VendorAssignedProvider> = new Map<
    number,
    VendorAssignedProvider
  >();

  for (const provider of providerAssignments) {
    const vendorProviders: VendorAssignedProvider = assignedProvidersByVendorId.get(
      provider.vendor_id,
    ) || { vendor_id: provider.vendor_id, vendor_name: provider.vendor_name, providers: [] };
    vendorProviders.providers.push(provider);
    assignedProvidersByVendorId.set(vendorProviders.vendor_id, vendorProviders);
  }

  return {
    account_info: {
      account_data: getAccountInfoResponse.account_info.account_data,
      assignments: providerAssignments,
      assignedProvidersByVendor: assignedProvidersByVendorId,
    },
  };
};

const getAccountCampaignInfo = async (
  apiClient: ApiClient,
  providerIDs: number[],
): Promise<GetProviderPortalCampaignAccountInfoResponse> => {
  return apiClient.providerPortalApi.deliveryProviderPortal.getCampaignsPermissionsForProviders({
    provider_ids: providerIDs,
  });
};

const getAccountInfoEpic = (
  action$: Observable<GlobalAppActions>,
  _state$: Observable<GlobalAppState>,
  { apiClient }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(requestAccountInfoAsyncAction.request)),
    switchMap(() =>
      from(getAccountInfo(apiClient)).pipe(
        map((accountInfo) => requestAccountInfoAsyncAction.success(accountInfo)),
        catchError((err) => of(requestAccountInfoAsyncAction.failure(err))),
      ),
    ),
  );
};

const getAccountCampaignInfoEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(requestAccountCampaignInfoAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([_, state]) => {
      const providerIDs = selectAssignedProviderIds(state);
      return from(getAccountCampaignInfo(apiClient, providerIDs)).pipe(
        map((campaignInfo) => requestAccountCampaignInfoAsyncAction.success(campaignInfo)),
        catchError((err) => of(requestAccountCampaignInfoAsyncAction.failure(err))),
      );
    }),
  );
};

const triggerCampaignInfoRequestEpic = (
  action$: Observable<GlobalAppActions>,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(requestAccountInfoAsyncAction.success)),
    map(() => requestAccountCampaignInfoAsyncAction.request()),
  );
};
const selectProviderEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(selectProviderAction)),
    withLatestFrom(state$),
    switchMap(() => {
      /*
       * On selected provider change, we need to re-fetch data like history, analytics, etc.
       *
       * Since the associated redux actions of these live within the stores of different lattice modules,
       * a page reload is needed to force a re-render and therefore re-fetch of data,
       * for whatever page the user might be viewing.
       * */
      window.location.reload();
      return of(
        requestTargetingParametersAsyncAction.request(),
        getScheduleAsyncAction.request(),
        getMainEntryWidgetUrlAsyncAction.request(),
      );
    }),
  );
};

export const accountEpic = combineEpics(
  selectProviderEpic,
  getAccountInfoEpic,
  getAccountCampaignInfoEpic,
  triggerCampaignInfoRequestEpic,
);
